/**
 * Card Product Component
 * Handles product card interactions: image gallery, swatches, variant selection, quick add
 * 
 * @requires ThemeUtils (utils.js) - optional
 */

class CardProduct {
  /**
   * Initialize all card products on the page
   */
  static initializeAll() {
    document.querySelectorAll('.card-product').forEach(card => {
      CardProduct.initialize(card);
    });
    
    // Initialize quick add buttons
    CardProduct.initializeQuickAdd();
    
    // Initialize size variant selectors
    CardProduct.initializeSizeSelectors();
  }

  /**
   * Initialize a specific card product
   * @param {HTMLElement} card - The card element to initialize
   */
  static initialize(card) {
    // Skip if already initialized
    if (card.dataset.cardProductInitialized === 'true') return;

    const mediaWrapper = card.querySelector('.card-product__media-wrapper');
    const slides = card.querySelectorAll('.card-product__media-slide');
    const leftChevron = card.querySelector('.card-product__chevron--left button');
    const rightChevron = card.querySelector('.card-product__chevron--right button');
    const swatches = card.querySelectorAll('.card-product__swatch');

    // Initialize image loading detection
    CardProduct.initializeImageLoading(card, mediaWrapper);

    if (slides.length === 0) return;

    let currentSlide = 0;

    /**
     * Show a specific slide
     */
    const showSlide = (index) => {
      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
      });
      currentSlide = index;

      // Update corresponding swatch selection
      const activeSlide = slides[index];
      const mediaId = activeSlide?.dataset.mediaId;
      if (mediaId) {
        swatches.forEach(swatch => {
          swatch.classList.toggle('is-selected', swatch.dataset.mediaId === mediaId);
        });
      }
    };

    // Chevron controls
    if (leftChevron && rightChevron && slides.length > 1) {
      // Detect if hover second-image is present in this card
      const hoverImageEl = card.querySelector('.card-product__hover-image');

      leftChevron.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        // If hover image is showing (user sees image 2) and hasn't navigated yet,
        // sync currentSlide to 1 so navigation feels correct
        if (hoverImageEl && !card.classList.contains('card-product--navigated')) {
          currentSlide = 1;
        }
        const newIndex = (currentSlide - 1 + slides.length) % slides.length;
        showSlide(newIndex);
        card.classList.add('card-product--navigated');
      });

      rightChevron.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (hoverImageEl && !card.classList.contains('card-product--navigated')) {
          currentSlide = 1;
        }
        const newIndex = (currentSlide + 1) % slides.length;
        showSlide(newIndex);
        card.classList.add('card-product--navigated');
      });

      // Reset navigated state when mouse leaves so hover image works on re-hover
      card.addEventListener('mouseleave', () => {
        if (card.classList.contains('card-product--navigated')) {
          // Don't reset if the user explicitly selected a size variant
          const hasSelectedSize = card.querySelector('.card-product__size-btn.is-selected[data-variant-id]');
          if (hasSelectedSize) return;
          card.classList.remove('card-product--navigated');
          // Reset to first slide so hover overlay (image 2) is consistent on next hover
          if (hoverImageEl) {
            showSlide(0);
          }
        }
      });
    }

    // Swatch controls - also update quick add button variant
    const hoverImageElForSwatches = card.querySelector('.card-product__hover-image');
    swatches.forEach(swatch => {
      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const mediaId = swatch.dataset.mediaId;
        const variantId = swatch.dataset.variantId;
        
        if (mediaId) {
          const targetSlide = card.querySelector(`.card-product__media-slide[data-media-id="${mediaId}"]`);
          if (targetSlide) {
            const slideIndex = Array.from(slides).indexOf(targetSlide);
            showSlide(slideIndex);
            // Hide hover overlay so the selected slide is visible
            if (hoverImageElForSwatches) {
              card.classList.add('card-product--navigated');
            }
          }
        }

        // Update quick add button variant ID
        if (variantId) {
          card.querySelectorAll('[data-add-to-cart]').forEach(btn => {
            btn.dataset.variantId = variantId;
          });
        }

        // Update swatch selection
        swatches.forEach(s => s.classList.remove('is-selected'));
        swatch.classList.add('is-selected');
      });
    });

    // Mark as initialized
    card.dataset.cardProductInitialized = 'true';
  }

  /**
   * Initialize image loading detection
   * Adds 'is-loaded' class to media wrapper when first image loads
   * @param {HTMLElement} card - The card element
   * @param {HTMLElement} mediaWrapper - The media wrapper element
   */
  static initializeImageLoading(card, mediaWrapper) {
    if (!mediaWrapper) return;

    const firstImage = card.querySelector('.card-product__media');
    if (!firstImage) {
      // No images, mark as loaded immediately
      mediaWrapper.classList.add('is-loaded');
      return;
    }

    // Check if already loaded (cached)
    if (firstImage.complete && firstImage.naturalHeight !== 0) {
      mediaWrapper.classList.add('is-loaded');
      return;
    }

    // Wait for image to load
    firstImage.addEventListener('load', () => {
      mediaWrapper.classList.add('is-loaded');
    }, { once: true });

    // Handle error - still hide shimmer
    firstImage.addEventListener('error', () => {
      mediaWrapper.classList.add('is-loaded');
    }, { once: true });
  }

  /**
   * Initialize size variant selectors on product cards
   */
  static initializeSizeSelectors() {
    document.addEventListener('click', (e) => {
      const sizeBtn = e.target.closest('.card-product__size-btn');
      if (!sizeBtn || sizeBtn.disabled) return;

      e.preventDefault();
      e.stopPropagation();

      const card = sizeBtn.closest('.card-product');
      if (!card) return;

      // Update selected state
      card.querySelectorAll('.card-product__size-btn').forEach(btn => btn.classList.remove('is-selected'));
      sizeBtn.classList.add('is-selected');

      const variantId = sizeBtn.dataset.variantId;
      const variantPrice = sizeBtn.dataset.variantPrice;
      const mediaId = sizeBtn.dataset.mediaId;

      // Switch to the variant's image if it has one
      if (mediaId) {
        const slides = card.querySelectorAll('.card-product__media-slide');
        const targetSlide = card.querySelector(`.card-product__media-slide[data-media-id="${mediaId}"]`);
        if (targetSlide && slides.length > 0) {
          slides.forEach(slide => slide.classList.remove('is-active'));
          targetSlide.classList.add('is-active');
          // Hide hover overlay so the selected slide is visible
          if (card.querySelector('.card-product__hover-image')) {
            card.classList.add('card-product--navigated');
          }
        }
      }

      // Update all add-to-cart buttons in this card
      card.querySelectorAll('[data-add-to-cart]').forEach(btn => {
        btn.dataset.variantId = variantId;
      });

      // Update product links to navigate to the selected variant
      card.querySelectorAll('a[href]').forEach(link => {
        try {
          const url = new URL(link.href, window.location.origin);
          url.searchParams.set('variant', variantId);
          link.href = url.pathname + url.search;
        } catch (e) {}
      });

      // Update price in buttons if shown
      if (variantPrice) {
        card.querySelectorAll('.card-product__btn-price').forEach(el => {
          el.textContent = variantPrice;
        });
      }
    });
  }

  /**
   * Initialize quick add to cart buttons
   */
  static initializeQuickAdd() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-add-to-cart]');
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();

      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      // Set loading state
      btn.classList.add('is-loading');
      btn.disabled = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: parseInt(variantId, 10),
            quantity: 1
          })
        });

        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }

        const data = await response.json();

        // Dispatch custom event for cart update
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item: data } }));

        // Update cart and open drawer using existing cart manager
        if (window.cartManager && typeof window.cartManager.loadCartData === 'function') {
          // loadCartData(true) refreshes cart HTML AND opens the drawer
          await window.cartManager.loadCartData(true);
        } else {
          // Fallback: update count and open drawer manually
          CardProduct.updateCartCount();
          CardProduct.openCartDrawer();
        }

      } catch (error) {
        console.error('Quick add error:', error);
        CardProduct.showNotification(window.theme?.strings?.cartError || 'Error adding to cart', 'error');
      } finally {
        // Remove loading state
        btn.classList.remove('is-loading');
        btn.disabled = false;
      }
    });
  }

  /**
   * Update cart count in header (fallback if cartManager not available)
   */
  static async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update cart count elements
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? '' : 'none';
      });
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  /**
   * Open cart drawer (fallback if cartManager not available)
   */
  static openCartDrawer() {
    // Try clicking the cart toggle button (same method as cartManager)
    const cartButton = document.querySelector('[data-drawer-toggle="cart"]');
    if (cartButton) {
      cartButton.click();
      return;
    }
    
    // Fallback: dispatch openDrawer event
    document.dispatchEvent(new CustomEvent('openDrawer', { 
      detail: { drawerId: 'cart', position: 'right' } 
    }));
  }

  /**
   * Show notification message
   * @param {string} message - Message to display
   * @param {string} type - 'success' or 'error'
   */
  static showNotification(message, type = 'success') {
    const notification = document.createElement('div');
    notification.className = `quick-add-notification quick-add-notification--${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      padding: 12px 20px;
      background: ${type === 'error' ? '#ff4444' : '#333'};
      color: white;
      border-radius: 4px;
      z-index: 10000;
      font-size: 14px;
      animation: slideIn 0.3s ease;
    `;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => notification.remove(), 300);
    }, 2500);
  }
}

// Expose for global access
window.CardProduct = CardProduct;

/**
 * Global utility function for easy initialization
 * @param {HTMLElement} container - Optional container to scope initialization
 */
window.initializeCardProducts = function(container) {
  if (!container) {
    CardProduct.initializeAll();
  } else {
    container.querySelectorAll('.card-product').forEach(card => {
      CardProduct.initialize(card);
    });
  }
};

/**
 * Main Product Page Component
 * Handles product page: variant selection, gallery, add to cart
 */
class MainProduct {
  constructor() {
    this.variantInput = document.querySelector('.main-product__variant-id');
    this.swatches = document.querySelectorAll('.main-product__swatch');
    this.sizes = document.querySelectorAll('.main-product__size-button');
    this.product = window.product || window.Shopify?.product;
    this.gallerySlides = document.querySelectorAll('.product-gallery__slide');
    
    if (!this.product) return;
    
    this.init();
  }

  init() {
    this.bindSwatches();
    this.bindSizes();
    this.bindGalleryControls();
    this.loadFromUrl();
  }

  /**
   * Update add to cart button state
   */
  updateAddToCartState(enabled) {
    const addBtn = document.querySelector('.main-product__add-button');
    if (addBtn) {
      addBtn.disabled = !enabled;
      addBtn.classList.toggle('is-disabled', !enabled);
    }
  }

  /**
   * Get selected color from swatches
   */
  getSelectedColor() {
    const selected = document.querySelector('.main-product__swatch.is-selected');
    return selected?.getAttribute('title') || null;
  }

  /**
   * Get selected size
   */
  getSelectedSize() {
    const selected = document.querySelector('.main-product__size-button.is-selected');
    return selected?.dataset.option2 || null;
  }

  /**
   * Find and select variant by current options
   */
  selectVariantByOptions() {
    const color = this.getSelectedColor();
    const size = this.getSelectedSize();

    if (!color || !size) {
      this.updateAddToCartState(false);
      return;
    }

    const match = this.product.variants.find(v =>
      v.option1?.toLowerCase() === color.toLowerCase() &&
      v.option2 === size
    );

    if (match) {
      if (this.variantInput) {
        this.variantInput.value = match.id;
      }
      
      // Update URL without reload
      const url = new URL(window.location.href);
      url.searchParams.set('variant', match.id);
      history.replaceState({}, '', url.toString());

      this.updateAddToCartState(match.available);
    } else {
      this.updateAddToCartState(false);
    }
  }

  /**
   * Update gallery swatch selection
   */
  updateGallerySwatchSelection(mediaId) {
    document.querySelectorAll('.product-gallery__swatch').forEach(s => {
      s.classList.toggle('is-selected', s.dataset.mediaId === mediaId);
    });
  }

  /**
   * Show specific gallery slide
   */
  showSlide(targetIndex) {
    this.gallerySlides.forEach((slide, i) => {
      const isActive = i === targetIndex;
      slide.classList.toggle('is-active', isActive);
      if (isActive) {
        this.updateGallerySwatchSelection(slide.dataset.mediaId);
      }
    });
  }

  /**
   * Bind swatch click events
   */
  bindSwatches() {
    this.swatches.forEach(swatch => {
      swatch.addEventListener('click', () => {
        this.swatches.forEach(s => s.classList.remove('is-selected'));
        swatch.classList.add('is-selected');
        
        const mediaId = swatch.dataset.mediaId;
        if (mediaId) {
          const gallerySlide = document.querySelector(`.product-gallery__slide[data-media-id="${mediaId}"]`);
          this.gallerySlides.forEach(slide => slide.classList.remove('is-active'));
          if (gallerySlide) gallerySlide.classList.add('is-active');
          this.updateGallerySwatchSelection(mediaId);
        }

        this.selectVariantByOptions();
      });
    });
  }

  /**
   * Bind size button click events
   */
  bindSizes() {
    this.sizes.forEach(button => {
      button.addEventListener('click', () => {
        this.sizes.forEach(b => b.classList.remove('is-selected'));
        button.classList.add('is-selected');
        this.selectVariantByOptions();
      });
    });
  }

  /**
   * Bind gallery chevron controls
   */
  bindGalleryControls() {
    document.querySelectorAll('.product-gallery').forEach(gallery => {
      const slides = gallery.querySelectorAll('.product-gallery__slide');
      const left = gallery.querySelector('.product-gallery__chevron--left');
      const right = gallery.querySelector('.product-gallery__chevron--right');

      if (!left || !right || slides.length < 2) return;

      let current = 0;

      left.addEventListener('click', () => {
        current = (current - 1 + slides.length) % slides.length;
        this.showSlide(current);
      });
      
      right.addEventListener('click', () => {
        current = (current + 1) % slides.length;
        this.showSlide(current);
      });
    });
  }

  /**
   * Load variant from URL or select defaults
   */
  loadFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const variantFromUrl = params.get('variant');

    if (variantFromUrl) {
      const matchedVariant = this.product.variants.find(v => 
        v.id.toString() === variantFromUrl
      );
      
      if (matchedVariant) {
        const color = matchedVariant.option1;
        const size = matchedVariant.option2;

        const swatchToSelect = Array.from(this.swatches).find(s => 
          s.getAttribute('title') === color
        );
        const sizeToSelect = Array.from(this.sizes).find(s => 
          s.dataset.option2 === size
        );

        if (swatchToSelect) swatchToSelect.click();
        if (sizeToSelect) sizeToSelect.click();
      }
    } else {
      // Select first options as default
      if (this.swatches.length > 0) this.swatches[0].click();
      if (this.sizes.length > 0) this.sizes[0].click();
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Initialize all card products
  CardProduct.initializeAll();
  
  // Initialize main product page if present
  if (document.querySelector('.main-product')) {
    window.mainProduct = new MainProduct();
  }
});